import { router, resolveRouterPath } from '../../router';
import { viewerCanOpen } from '../../utility/viewer-route-access';
import { GLASS_CERTIFICATES_TAB_ID } from './glass-workspace-tabs';

/**
 * Glass Accreditation's routes, built in one place for every host of the certificate
 * surfaces (the module's own pages and the company / site workspace tabs), so that a
 * certificate opens at the same address wherever it was clicked.
 *
 * Components emit navigation intent and never route; these are what the hosts call.
 */

/** The certificate types the module knows, as the API spells them. */
export type GlassCertificateType = 'AS2208' | 'AS4666';

/**
 * The module's certificate register: every company's and site's certificates, the root of
 * every certificate trail and the way back from a company's or site's own tab (architect,
 * 2026-09-07: "a link back to the main glass accreditation page for all companies and sites
 * now that we've hidden the By Company feature").
 */
export function certificateRegisterHref(): string {
  return '/module/glass-accreditation/certificates';
}

/**
 * Whether the signed-in person may open the register at all. The workspace tabs are staff
 * routes, and staff are not all glass staff: the register needs a glass domain, so a host
 * that offered the link to everyone would send some of them to "not allowed".
 */
export function canOpenCertificateRegister(): boolean {
  return viewerCanOpen(certificateRegisterHref());
}

/** A certificate's detail page, by type and id. */
export function certificateHref(type: string, id: string): string {
  return `/module/glass-accreditation/${type.toLowerCase()}/${id}`;
}

/**
 * The create page for a certificate type. A site preselects the form's site; failing that a
 * company narrows the form's site choice to that company's sites (the create pages read
 * `site` and `company` from the query).
 */
export function certificateCreateHref(
  type: GlassCertificateType,
  scope: { siteId?: string; companyId?: string }
): string {
  const base = `/module/glass-accreditation/${type.toLowerCase()}/create`;
  if (scope.siteId) return `${base}?site=${scope.siteId}`;
  if (scope.companyId) return `${base}?company=${scope.companyId}`;
  return base;
}

/**
 * A site's own certificates tab on its workspace (architect, 2026-09-06: from the company's
 * certificates "have a link to see them directly on the site"). The tab id is the site
 * workspace module id registered in domains/glass-processor/register.ts.
 */
export function siteCertificatesHref(
  groupId: string,
  loginSiteId: string
): string {
  return resolveRouterPath(
    `maintenance/companies/${groupId}/sites/${loginSiteId}/${GLASS_CERTIFICATES_TAB_ID}`
  );
}

/** Route in-SPA, falling back to a history push when no router is mounted (tests). */
export function navigateTo(href: string): void {
  if (router) void router.navigate(href);
  else window.history.pushState({}, '', href);
}
